import { useI18n } from '../i18n/useI18n'
import type { Survivor, SurvivorRole } from '../game/types'
import { getPortraitSrc } from '../game/portraits'
import { SURVIVOR_PROGRESS_SKILL } from '../game/skills'
import { LevelBadge } from './LevelBadge'
import { StatusBadge } from './StatusBadge'

const ROLE_KEYS: Record<
  SurvivorRole,
  'role_scavenger' | 'role_medic' | 'role_cook' | 'role_trader'
> = {
  Scavenger: 'role_scavenger',
  Medic: 'role_medic',
  Cook: 'role_cook',
  Trader: 'role_trader',
}

const NEED_DOT = (v: number) =>
  v < 20
    ? 'bg-red-500'
    : v < 40
      ? 'bg-amber-500'
      : 'bg-emerald-500'

type Props = {
  survivor: Survivor
  onClick: (id: string) => void
  statusLabelOverride?: string
  statusToneOverride?: string
}

export function SurvivorRow({ survivor, onClick, statusLabelOverride, statusToneOverride }: Props) {
  const { t } = useI18n()
  const initials = survivor.name.slice(0, 2).toUpperCase()
  const portraitSrc = getPortraitSrc(survivor.portrait)
  const level = survivor.skills[SURVIVOR_PROGRESS_SKILL]?.level ?? 1
  const lowestNeed = Math.min(
    survivor.needs.hunger,
    survivor.needs.thirst,
    survivor.needs.energy,
  )
  return (
    <button
      type="button"
      onClick={() => onClick(survivor.id)}
      className="flex w-full items-center gap-3 rounded-lg border border-stone-800 bg-stone-800/70 p-2.5 text-left transition-colors hover:border-amber-800 hover:bg-stone-800"
    >
      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-gradient-to-br from-stone-800 to-stone-950">
        {portraitSrc ? (
          <img src={portraitSrc} alt={survivor.name} className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center font-mono text-sm font-bold text-stone-300">
            {initials}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-stone-100">
            {survivor.name}
          </span>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${NEED_DOT(lowestNeed)}`} />
        </div>
        <div className="text-[10px] uppercase tracking-wider text-stone-500">
          {t(ROLE_KEYS[survivor.role])}
        </div>
      </div>
      <LevelBadge level={level} />
      <StatusBadge status={survivor.status} labelOverride={statusLabelOverride} toneOverride={statusToneOverride} />
      <span className="text-stone-600">›</span>
    </button>
  )
}
